import Block, { Theme } from '@/components/Block'
import Button, { ButtonTheme } from '@/components/Button'
import Layout, { Stack } from '@/components/Layout'
import Slider, { useSliderRef } from '@/components/Slider'
import Text from '@/components/Text'
import {
  coreLayoutContainer,
  coreLayoutSection,
  coreSectionPaddingClassesBottomHalf,
  coreSectionPaddingClassesTopHalf,
  rbp,
  UnitSize,
} from '@/constants/measured-scope'
import { PageInfo, SectionNavigationAction } from '@/shared/api-generated-types'
import CardViewClass from '@/widgets/CardViewClass'
import { Props as ClassCardProps } from '@/widgets/ClassCard'
import InstructorCard, { Props as InstructorCardProps } from '@/widgets/InstructorCard'
import SeriesCard, { Props as SeriesCardProps } from '@/widgets/SeriesCard'
import { useMediaQuery } from '@react-hookz/web'
import uniq from 'lodash/uniq'
import React, { useEffect, useMemo, useState } from 'react'

interface CTAProps {
  url: string
  label: string
}

type CarouselOptions = false | UnitSize[] | 'always'

export interface Props {
  title?: string | null
  subtitle?: string | null
  theme?: Theme
  cta?: CTAProps
  carousel?: CarouselOptions
  items: Partial<ClassCardProps | SeriesCardProps | InstructorCardProps>[]
  spacing?: UnitSize
  cardComponent?: 'ClassCard' | 'SeriesCard' | 'InstructorCard'
  cardType?: 'standard' | 'feed'
  // Callback to parent with page to load if applicable
  onLoadMore?: undefined | (() => void)
  pageInfo?: Partial<PageInfo>
  fetching?: boolean
  className?: string
  navigationActions?: SectionNavigationAction[]
}

// Which button themes should be used
// with the block theme that is selected
const buttonThemeMap: { [x: string]: ButtonTheme } = {
  primary: 'white',
  secondary: 'glass',
  tertiary: 'white',
  white: 'primaryOutline',
}

// Gets the appropriate JSX.Element as per the carouselOptions

export type DeviceSizes = {
  [key in UnitSize]: boolean | undefined
}

const getElement = (
  elemPrimary: JSX.Element | null,
  elemAlternative: JSX.Element | null,
  carousel: CarouselOptions,
  deviceSize: DeviceSizes,
): JSX.Element | null => {
  if (carousel === 'always') {
    return elemAlternative
  }
  if (!carousel || !Array.isArray(carousel)) {
    return elemPrimary
  }
  if (carousel.includes('lg') && deviceSize.lg) {
    return elemAlternative
  }
  if (carousel.includes('md') && deviceSize.md) {
    return elemAlternative
  }
  if (carousel.includes('sm') && deviceSize.sm) {
    return elemAlternative
  }
  return elemPrimary
}

const CardBlock = ({
  title,
  subtitle,
  spacing = 'sm',
  theme = 'white',
  navigationActions,
  carousel = false,
  cardComponent = 'ClassCard',
  cardType = 'feed',
  items,
  pageInfo,
  onLoadMore,
  fetching,
  className = '',
}: Props): JSX.Element => {
  const { ref: sliderRef, next, prev } = useSliderRef()
  const [cards, setCards] = useState<Array<React.ReactNode | null>>([])
  const canShowLoadMore = onLoadMore && pageInfo?.hasNextPage // 'Load more' is opt-in, by providing the callback. Also checks there's a new page to load first.
  const isSmallDevice = useMediaQuery(`only screen and (min-width : ${rbp.smallTablet})`, {
    initializeWithValue: false,
  })
  const isMediumDevice = useMediaQuery(`only screen and (min-width : ${rbp.tablet})`, { initializeWithValue: false })
  const isLargeDevice = useMediaQuery(`only screen and (min-width : ${rbp.desktop})`, { initializeWithValue: false })

  const newItems = items
    ? items.map((item) => {
        // All card event tracking should occur here in the future to avoid props drilling.
        // Search for GUID to find all implementations: 70e7b1d8-0cb9-4883-8927-1a69372cfac2
        if (cardComponent === 'ClassCard') {
          return <CardViewClass key={`class_${item.id}`} {...(item as ClassCardProps)} />
        }
        if (cardComponent === 'SeriesCard')
          return <SeriesCard key={`class_${item.id}`} {...(item as SeriesCardProps)} />
        if (cardComponent === 'InstructorCard')
          return <InstructorCard key={`class_${item.id}`} {...(item as InstructorCardProps)} />
        return null
      })
    : []

  /*
   * Set new section items, whilst accounting for 'load more'
   * functionality. Simply sets the items when none already exist
   * otherwise it checks that we're not on the first page, and
   * there's already a previous state before blending.
   */
  useEffect(() => {
    setCards((prevState) => {
      if (pageInfo && pageInfo?.page !== 1 && prevState) {
        return uniq([...prevState, ...newItems])
      }
      return newItems
    })
  }, [items])

  const cardList = useMemo(() => {
    const styleOverride = {
      '--grid-gap': `var(--element-spacing-${spacing})`,
    } as React.CSSProperties
    return getElement(
      <section className={coreLayoutSection}>
        <div
          style={cardType === 'standard' ? { ...styleOverride, rowGap: '3rem' } : styleOverride}
          className={`${coreLayoutContainer} grid`}
        >
          {cards}
        </div>
      </section>,
      <Slider
        items={cards}
        spacing="sm"
        ref={sliderRef}
        shadowBleed="60px"
        trackClasses="ml-xxl-gte-desktop ml-lg-gte-tablet ml-sm-lte-smallTablet ml-lg"
      />,
      carousel,
      {
        lg: isLargeDevice,
        md: isMediumDevice,
        sm: isSmallDevice,
      },
    )
  }, [isLargeDevice, isMediumDevice, isSmallDevice, cards])

  const carouselArrows = useMemo(() => {
    return getElement(
      null,
      <div className="flex-center">
        {!!navigationActions && isSmallDevice && (
          <>
            {navigationActions.map((item) => {
              return (
                <div key={item.label} className="ta-c mr-sm">
                  <Button
                    label={item.label}
                    href={`/${item.targetPageId}`}
                    component="link"
                    theme={buttonThemeMap[theme]}
                  />
                </div>
              )
            })}
          </>
        )}
        <Button theme={buttonThemeMap[theme]} iconLeft="arrow-left" onClick={prev} />
        <Button theme={buttonThemeMap[theme]} className="ml-sm" iconRight="arrow-right" onClick={next} />
      </div>,
      carousel,
      {
        lg: isLargeDevice,
        md: isMediumDevice,
        sm: isSmallDevice,
      },
    )
  }, [isLargeDevice, isMediumDevice, isSmallDevice])

  return (
    <Block
      theme={theme}
      spacing={spacing}
      className={`${className} ${
        !title && !carousel && !SeriesCard ? 'pl-0 pr-0 pt-md pt-lg-gte-md pt-xl-gte-md pt-xxl-gte-xxl' : 'p-0'
      }`}
    >
      {(title || carousel) && (
        <Layout spacing={spacing} className={coreSectionPaddingClassesTopHalf}>
          <Stack alignItems="center" x>
            {title && (
              <Text tag="h2" weight="lg" size="lg" responsiveSize={{ lg: 'xl' }}>
                {title}
              </Text>
            )}
            {carouselArrows}
          </Stack>
        </Layout>
      )}

      {subtitle && (
        <Text tag="p" size="xs" weight="sm" className="fc-grey">
          {subtitle}
        </Text>
      )}

      <div className={!!title ? 'pt' : ''}>{cardList}</div>
      {!!navigationActions && !isSmallDevice && (
        <>
          {navigationActions.map((item) => {
            return (
              <div key={item.label} className="pt ta-c">
                <Button
                  label={item.label}
                  component="link"
                  href={`/${item.targetPageId}`}
                  theme={item.theme ? buttonThemeMap[item.theme] : 'clear'}
                />
              </div>
            )
          })}
        </>
      )}
      {canShowLoadMore && (
        <div className={coreSectionPaddingClassesBottomHalf}>
          <div className="pt ta-c">
            <Button
              label="Load more"
              size="lg"
              onClick={!fetching ? onLoadMore : undefined}
              component="button"
              theme={buttonThemeMap[theme]}
              disabled={fetching}
            />
          </div>
        </div>
      )}
    </Block>
  )
}

export default CardBlock
